// BUILD-046 task 8.18: renders ONE list from its ListView (listStateModel.describeList). Exactly one of loading / error / empty / data is
// drawn: an error never shows the empty text or items, an empty list never shows an error (Req 15.4 - 15.10). The error block carries a
// Retry that re-requests THIS list only (the caller passes `onRetry`).
import type { ReactNode } from 'react';
import type { ListView } from './listStateModel';

export interface ListErrorBlockProps {
  heading: string;
  detail: string;
  onRetry: () => void;
  /** Disambiguates the Retry control for assistive technology and tests, for example "my tasks". */
  listLabel?: string;
}

export function ListErrorBlock({ heading, detail, onRetry, listLabel }: ListErrorBlockProps) {
  return (
    <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-2" data-testid="list-error">
      <div>
        <p className="font-medium">{heading}</p>
        {detail !== '' && <p className="text-xs mt-0.5">{detail}</p>}
      </div>
      <button type="button" onClick={onRetry} aria-label={listLabel ? `Retry loading ${listLabel}` : 'Retry'} className="px-3 py-1 text-xs font-medium rounded border border-red-300 bg-white hover:bg-red-100">
        Retry
      </button>
    </div>
  );
}

export interface ListStateViewProps<T> {
  view: ListView<T>;
  onRetry: () => void;
  /** Used for the Retry control's accessible name. */
  listLabel?: string;
  renderItems: (items: readonly T[]) => ReactNode;
}

export function ListStateView<T>({ view, onRetry, listLabel, renderItems }: ListStateViewProps<T>) {
  switch (view.kind) {
    case 'loading':
      return (
        <div className="py-8 text-center text-gray-500 text-sm" role="status" aria-live="polite" data-testid="list-loading">
          {view.text}
        </div>
      );
    case 'error':
      return <ListErrorBlock heading={view.heading} detail={view.detail} onRetry={onRetry} listLabel={listLabel} />;
    case 'empty':
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500 text-sm" data-testid="list-empty">
          {view.text}
        </div>
      );
    case 'data':
      return <>{renderItems(view.items)}</>;
  }
}
